<template>
  <div :class="[classes]">
    <div class="relative rounded-md shadow-sm">
      <Combobox
        v-model="selected_item"
        :disabled="disabled"
        :nullable="allowNull"
        :multiple="isMultiple"
        as="div"
        class="w-full"
        name="type">
        <ComboboxLabel v-if="showLabel && !floatingLabel" class="block text-base font-medium text-gray-700 dark:text-white" :for="name" />
        <ComboboxLabel v-if="description" class="block text-sm font-normal text-gray-500 dark:text-white" :for="name">
          {{ description }}
        </ComboboxLabel>
        <ComboboxLabel
          v-if="showLabel && floatingLabel"
          :class="[
            isInputFocused || selected_item ? '-top-3' : 'top-[5px]',
            !isBoxBackground
              ? 'bg-light-screen-background dark:bg-dark-screen-background'
              : 'bg-light-box-background dark:bg-dark-box-background',
            isInputFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
          ]"
          class="absolute z-10 flex items-center px-1 transition-all ease-in-out transform h-fit text-common-grey left-2 duration-side-menu-transition-duration"
          :for="name">
          {{ label }}

          <div
            v-if="tooltipInfo"
            class="p-0.5 w-4 h-4 bg-[#899399] flex items-center justify-center rounded-full text-white ml-2 mt-0.5 has-tooltip">
            ?
            <span
              class="inline-flex ml-6 mt-14 text-common-grey items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
              {{ tooltipInfo }}</span>
          </div>
        </ComboboxLabel>
        <div class="relative">
          <span v-if="icon" class="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
            <component :is="icon" class="w-5 h-5 text-gray-400" aria-hidden="true" />
          </span>
          <ComboboxInput
            class="pr-10"
            :class="[
              { 'text-common-grey': disableInput },
              { 'border-status-error-red': errorMessage },
              { 'bg-light-box-background dark:bg-dark-box-background': isBoxBackground },
              { 'pl-8': icon },
              inputClasses,
              'form-input dark:bg-dark-box-background !text-md'
            ]"
            :display-value="(item) => getLabel(item)"
            :disabled="disableInput || disabled"
            autocomplete="off"
            :placeholder="placeholder"
            @focusin="focusInput"
            @focusout="unfocusInput"
            @change="query = $event.target.value" />

          <ComboboxButton v-if="!disabled" class="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-none">
            <ChevronDown class="w-5 h-5 text-gray-800" aria-hidden="true" />
          </ComboboxButton>
          <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
            <ComboboxOptions
              v-if="items_clone?.length > 0"
              :class="[
                isBoxBackground
                  ? 'bg-light-box-background dark:bg-dark-box-background'
                  : 'bg-light-screen-background dark:bg-dark-screen-background',
                { 'right-0': alignRight },
                optionsClasses,
                { relative: relativePosition }
              ]"
              class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background dark:text-white max-h-56 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              <ComboboxOption
                v-if="selected_item && allowNull && filtered_items.length"
                key="reset"
                v-slot="{ active }"
                :value="null"
                @click="isMultiple ? (selected_item = []) : (selected_item = null)">
                <li
                  :class="[
                    active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white',
                    'relative py-2 pl-8 pr-4 w-full justify-end flex cursor-pointer'
                  ]">
                  <span class="block font-normal truncate">{{ translatedClearLabel }} </span>
                </li>
              </ComboboxOption>
              <ComboboxOption
                v-for="item in filtered_items"
                :key="item[labelAttr]"
                v-slot="{ active, selected }"
                as="template"
                :value="valueAttr ? item[valueAttr] : item">
                <slot name="item" :item="item" :active="active">
                  <li :class="[active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white', 'cursor-default relative py-2 pl-8 pr-4']">
                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                      {{ getItemLabel(item) }}
                    </span>
                    <span
                      v-if="selected"
                      :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                      <Check class="w-5 h-5" aria-hidden="true" />
                    </span>
                  </li>
                </slot>
              </ComboboxOption>
            </ComboboxOptions>
          </transition>

          <div v-show="errorMessage" class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg class="w-5 h-5 text-red-400"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 20 20"
                 fill="currentColor"
                 aria-hidden="true">
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      </Combobox>

      <p
        class="first-letter:capitalize absolute right-0 leading-tight pt-[1px] text-[12px] text-red-400"
        :class="[errorMessage || meta.valid ? 'visible' : 'invisible']">
        {{ errorMessage || successMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import { useField } from "vee-validate"
import { ChevronDown, Check } from "lucide-vue-next"
import { Combobox, ComboboxButton, ComboboxInput, ComboboxLabel, ComboboxOption, ComboboxOptions } from "@headlessui/vue"
import { utils } from "@/store/helpers"
import { toRef } from "vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Combobox,
    ComboboxLabel,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
    ComboboxInput,
    ChevronDown,
    Check
  },
  props: {
    relativePosition: null,
    tooltipInfo: null,
    icon: null,
    items: {
      type: Array,
      required: true
    },
    showLabel: {
      type: Boolean,
      default: false
    },
    returnValue: {
      type: Boolean,
      default: false
    },
    disableInput: {
      type: Boolean,
      default: false
    },
    floatingLabel: {
      type: Boolean,
      default: true
    },
    isBoxBackground: {
      type: Boolean,
      default: true
    },
    allowTypedValue: {
      type: Boolean,
      default: false
    },
    alignRight: {
      type: Boolean,
      default: false
    },
    inputClasses: {
      type: String,
      default: ""
    },
    classes: {
      type: String,
      default: "text"
    },
    description: {
      type: String,
      default: ""
    },
    optionsClasses: {
      type: String,
      default: ""
    },
    // eslint-disable-next-line vue/no-unused-properties

    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    clearLabel: {
      type: String,
      default: "Clear",
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    labelAttr: {
      type: String,
      validator(v) {
        return v !== undefined
      }
    },
    valueAttr: {
      validator(v) {
        return v !== undefined
      }
    },
    value: {
      default: null,
      type: [String, Number, Boolean]
    },
    modelValue: null,
    successMessage: {
      type: String,
      default: ""
    },
    allowNull: {
      type: Boolean,
      default: true
    },
    isMultiple: {
      type: Boolean,
      default: false
    },
    clearInput: {
      type: Boolean,
      default: false,
      required: false
    },
    objectType: {
      type: String,
      required: false
    },
    createGroupAction: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:currentItem", "update:modelValue"],
  setup(props) {
    const name = toRef(props, "name")
    const {
      value: selected_item,
      errorMessage,
      meta
    } = useField(name, undefined, {
      initialValue: props.value || props.modelValue
    })

    return {
      errorMessage,
      selected_item,
      meta
    }
  },
  data() {
    return {
      items_clone: [],
      is_input_label_up: false,
      query: "",
      isInputFocused: false,
      prevent_listener: false
    }
  },
  computed: {
    translatedClearLabel() {
      return this.$t(this.clearLabel)
    },
    filtered_items() {
      return this.scoreSearch()
    }
  },
  watch: {
    value(newValue) {
      this.selected_item = newValue
    },
    modelValue(newValue) {
      this.selected_item = newValue
    },
    clearInput() {
      if (this.clearInput) {
        this.selected_item = null
      }
    },
    items() {
      this.items_clone = this.items ? this.$utils.clone(this.items) : []
      if (!this.isMultiple && this.items_clone.findIndex((item) => this.getValue(this.selected_item) == this.getValue(item)) == -1) {
        this.selected_item = null
      }
      if (this.createGroupAction) {
        this.items_clone.unshift({ id: "create_group", name: "Create new Group" })
      }
    },
    query() {
      if (this.allowTypedValue) {
        this.selected_item = this.query
      }
    },

    selected_item() {
      if (!this.prevent_listener) {
        if (this.selected_item == "create_group") {
          this.selected_item = null
          this.$emitter.emit(ALL_EMITTERS.CREATE_NEW_GROUP, this.objectType)
        } else if (this.selected_item) {
          if (this.returnValue) {
            if (this.isMultiple) {
              this.$emit(
                "update:currentItem",
                this.selected_item.map((x) => this.getValue(x))
              )
              this.$emit(
                "update:modelValue",
                this.selected_item.map((x) => this.getValue(x))
              )
            } else {
              this.$emit("update:currentItem", this.getValue(this.selected_item))
              this.$emit("update:modelValue", this.getValue(this.selected_item))
            }
          } else {
            this.$emit("update:currentItem", this.selected_item)
            this.$emit("update:modelValue", this.selected_item)
          }
        } else {
          this.$emit("update:currentItem", null)
          this.$emit("update:modelValue", null)
          return null
        }
      }
    }
  },
  created() {
    this.items_clone = this.$utils.clone(this.items)
    if (this.createGroupAction) {
      this.items_clone.unshift({ id: "create_group", name: "Create new Group" })
    }
    if (this.modelValue) {
      this.prevent_listener = true
      this.selected_item = this.$utils.clone(this.modelValue)
      if (this.isMultiple) {
        this.selected_item = Array.isArray(this.modelValue)
          ? this.$utils.clone(this.modelValue)
          : this.$utils.clone(this.modelValue.split(","))
      }
      setTimeout(() => {
        this.prevent_listener = false
      }, 600)
    }
  },
  methods: {
    scoreSearch() {
      if (!this.query || this.query.trim() === "") {
        return this.items_clone
      }

      const query = this.query.toLowerCase().trim()

      // Calculate relevance score for each item
      return this.items_clone
        .map((item) => {
          const label = this.getItemLabel(item)?.toLowerCase() || ""
          if (!label.includes(query)) {
            return null // Filter out non-matching items
          }

          // Calculate relevance score
          let score = 0

          // Exact match gets highest score
          if (label === query) {
            score += 100
          }

          // Starts with query gets high score
          if (label.startsWith(query)) {
            score += 50
          }

          // Word boundary match gets medium score
          const wordBoundaryMatch = new RegExp(`\\b${query}`, "i").test(label)
          if (wordBoundaryMatch) {
            score += 25
          }

          // Position of match affects score (earlier = better)
          const position = label.indexOf(query)
          if (position >= 0) {
            score += (100 - Math.min(position, 100)) * 0.1
          }

          // Ratio of query length to total length (higher ratio = better match)
          score += (query.length / label.length) * 10

          return { item, score }
        })
        .filter(Boolean) // Remove null items
        .sort((a, b) => b.score - a.score) // Sort by score (descending)
        .map((result) => result.item) // Return just the items
    },
    focusInput() {
      this.isInputFocused = true
      this.$utils.debounce(() => {
        this.is_input_label_up = true
      }, 100)
    },
    unfocusInput() {
      this.isInputFocused = false
      this.is_input_label_up = false
    },
    getValue(item) {
      if (typeof item == "object" && item && this.valueAttr) {
        return item[this.valueAttr]
      }
      return item
    },
    getItemLabel(item) {
      if (typeof item == "object" && item && this.labelAttr) {
        return item[this.labelAttr]
      }
      return item
    },
    getLabel(input_item) {
      if (!utils.isEmpty(input_item)) {
        if (this.isMultiple) {
          if (Array.isArray(input_item)) {
            return input_item

              .map((subitem) => {
                if (this.items_clone.findIndex((x) => this.getValue(x) == this.getValue(subitem)) != -1) {
                  const item = this.items_clone.find((x) => this.getValue(x) == this.getValue(subitem))

                  return item[this.labelAttr]
                } else {
                  return subitem
                }
              })
              .join(", ")
          } else {
            return input_item
              .split(",")
              .map((subitem) => {
                if (this.items_clone.findIndex((x) => this.getValue(x) == this.getValue(subitem)) != -1) {
                  const item = this.items_clone.find((x) => this.getValue(x) == this.getValue(subitem))

                  return item[this.labelAttr]
                } else {
                  return subitem
                }
              })
              .join(", ")
          }
        } else {
          if (this.items_clone.findIndex((x) => this.getValue(x) == this.getValue(input_item)) != -1) {
            const item = this.items_clone.find((x) => this.getValue(x) == this.getValue(input_item))

            return item[this.labelAttr]
          } else {
            if (this.labelAttr) {
              return ""
            } else {
              return input_item
            }
          }
        }
      } else {
        return ""
      }
    }
  }
}
</script>
